import { createContext, useContext, useState, useCallback, ReactNode } from "react";
import { useAuth } from "./AuthContext";

// ============ REFERRAL REWARD ENGINE ============

export type RewardType = "free_month" | "discount" | "cash_credit";
export type RewardStatus = "pending" | "earned" | "redeemed" | "expired";
export type ReferralStatus = "clicked" | "signed_up" | "subscribed";
export type RedemptionType = "subscription_discount" | "cash_payout" | "platform_credits";

export interface Referral {
  id: string;
  referrerUserId: string;
  refereeEmail: string;
  refereeName: string;
  status: ReferralStatus;
  planPurchased?: string;
  createdAt: string;
  signedUpAt?: string;
  subscribedAt?: string;
}

export interface Reward {
  id: string;
  userId: string;
  referralId: string;
  type: RewardType;
  status: RewardStatus;
  value: number;
  description: string;
  earnedAt: string;
  redeemedAt?: string;
  expiresAt: string;
}

export interface WalletTransaction {
  id: string;
  userId: string;
  type: "credit" | "debit";
  amount: number;
  description: string;
  referralId?: string;
  redemptionType?: RedemptionType;
  createdAt: string;
}

export interface ReferralWallet {
  balance: number;
  totalEarned: number;
  totalRedeemed: number;
  transactions: WalletTransaction[];
}

export interface ReferralStats {
  totalReferrals: number;
  successfulSignups: number;
  activeSubscribers: number;
  totalRewardsEarned: number;
  pendingRewards: number;
  redeemedRewards: number;
  totalCashEarned: number;
  totalMonthsFree: number;
}

// Plan-based reward amounts
export const PLAN_REWARDS: Record<string, { amount: number; label: string }> = {
  starter: { amount: 100, label: "Starter Plan" },
  professional: { amount: 200, label: "Professional Plan" },
  enterprise: { amount: 500, label: "Enterprise Plan" },
};

interface ReferralContextType {
  referralCode: string;
  referralLink: string;
  referrals: Referral[];
  rewards: Reward[];
  stats: ReferralStats;
  wallet: ReferralWallet;
  addReferral: (email: string, name: string) => void;
  upgradeReferral: (id: string, status: ReferralStatus, plan?: string) => void;
  redeemReward: (id: string) => boolean;
  redeemWallet: (amount: number, type: RedemptionType) => boolean;
}

const REWARD_CONFIG: Record<ReferralStatus, { type: RewardType; value: number; description: string } | null> = {
  clicked: null,
  signed_up: { type: "cash_credit", value: 100, description: "₹100 credit for successful referral signup" },
  subscribed: { type: "free_month", value: 1, description: "1 free subscription month — referral converted!" },
};

// Seed data
const SEED_REFERRALS: Referral[] = [];
const SEED_REWARDS: Reward[] = [];
const SEED_WALLET_TXN: WalletTransaction[] = [];

function generateCode(name: string, id: string): string {
  return `${name.toLowerCase().replace(/[^a-z0-9]/g, "").slice(0, 10)}${id.slice(-4)}`;
}

const ReferralContext = createContext<ReferralContextType | null>(null);

export function ReferralProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [referrals, setReferrals] = useState<Referral[]>(SEED_REFERRALS);
  const [rewards, setRewards] = useState<Reward[]>(SEED_REWARDS);
  const [walletTxns, setWalletTxns] = useState<WalletTransaction[]>(SEED_WALLET_TXN);

  const referralCode = user ? generateCode(user.name, user.id) : "guest0000";
  const referralLink = `accoupro.com/ref/${referralCode}`;

  const myReferrals = referrals.filter(r => r.referrerUserId === user?.id);
  const myRewards = rewards.filter(r => r.userId === user?.id);
  const myTxns = walletTxns.filter(t => t.userId === user?.id);

  const walletBalance = myTxns.reduce((sum, t) => t.type === "credit" ? sum + t.amount : sum - t.amount, 0);
  const totalEarned = myTxns.filter(t => t.type === "credit").reduce((s, t) => s + t.amount, 0);
  const totalRedeemed = myTxns.filter(t => t.type === "debit").reduce((s, t) => s + t.amount, 0);

  const wallet: ReferralWallet = {
    balance: walletBalance,
    totalEarned,
    totalRedeemed,
    transactions: myTxns.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
  };

  const addReferral = useCallback((email: string, name: string) => {
    if (!user) return;
    const newRef: Referral = {
      id: `ref-${crypto.randomUUID().slice(0, 8)}`,
      referrerUserId: user.id,
      refereeEmail: email,
      refereeName: name,
      status: "clicked",
      createdAt: new Date().toISOString(),
    };
    setReferrals(prev => [newRef, ...prev]);
  }, [user]);

  const upgradeReferral = useCallback((id: string, status: ReferralStatus, plan?: string) => {
    setReferrals(prev => prev.map(r => {
      if (r.id !== id) return r;
      const updated = { ...r, status };
      if (status === "signed_up") updated.signedUpAt = new Date().toISOString();
      if (status === "subscribed") {
        updated.subscribedAt = new Date().toISOString();
        if (plan) updated.planPurchased = plan;
      }
      return updated;
    }));

    // Auto-generate legacy reward
    const config = REWARD_CONFIG[status];
    if (config && user) {
      const newReward: Reward = {
        id: `rw-${crypto.randomUUID().slice(0, 8)}`,
        userId: user.id,
        referralId: id,
        type: config.type,
        value: config.value,
        status: "earned",
        description: config.description,
        earnedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 180 * 86400000).toISOString(),
      };
      setRewards(prev => [newReward, ...prev]);
    }

    // Plan-based wallet reward on subscription
    if (status === "subscribed" && plan && user) {
      const planReward = PLAN_REWARDS[plan];
      if (planReward) {
        const ref = referrals.find(r => r.id === id);
        const txn: WalletTransaction = {
          id: `wt-${crypto.randomUUID().slice(0, 8)}`,
          userId: user.id,
          type: "credit",
          amount: planReward.amount,
          description: `Plan reward — ${ref?.refereeName || "Referral"} purchased ${planReward.label}`,
          referralId: id,
          createdAt: new Date().toISOString(),
        };
        setWalletTxns(prev => [txn, ...prev]);
      }
    }

    // Signup bonus to wallet
    if (status === "signed_up" && user) {
      const ref = referrals.find(r => r.id === id);
      const txn: WalletTransaction = {
        id: `wt-${crypto.randomUUID().slice(0, 8)}`,
        userId: user.id,
        type: "credit",
        amount: 100,
        description: `Signup bonus — ${ref?.refereeName || "Referral"} signed up`,
        referralId: id,
        createdAt: new Date().toISOString(),
      };
      setWalletTxns(prev => [txn, ...prev]);
    }
  }, [user, referrals]);

  const redeemReward = useCallback((id: string): boolean => {
    const reward = rewards.find(r => r.id === id);
    if (!reward || reward.status !== "earned") return false;
    setRewards(prev => prev.map(r => r.id === id ? { ...r, status: "redeemed" as RewardStatus, redeemedAt: new Date().toISOString() } : r));
    return true;
  }, [rewards]);

  const redeemWallet = useCallback((amount: number, type: RedemptionType): boolean => {
    if (amount <= 0 || amount > walletBalance || !user) return false;
    const labels: Record<RedemptionType, string> = {
      subscription_discount: "Redeemed as subscription discount",
      cash_payout: "Redeemed as cash payout",
      platform_credits: "Redeemed as platform credits",
    };
    const txn: WalletTransaction = {
      id: `wt-${crypto.randomUUID().slice(0, 8)}`,
      userId: user.id,
      type: "debit",
      amount,
      description: labels[type],
      redemptionType: type,
      createdAt: new Date().toISOString(),
    };
    setWalletTxns(prev => [txn, ...prev]);
    return true;
  }, [walletBalance, user]);

  const stats: ReferralStats = {
    totalReferrals: myReferrals.length,
    successfulSignups: myReferrals.filter(r => r.status === "signed_up" || r.status === "subscribed").length,
    activeSubscribers: myReferrals.filter(r => r.status === "subscribed").length,
    totalRewardsEarned: myRewards.filter(r => r.status === "earned" || r.status === "redeemed").length,
    pendingRewards: myRewards.filter(r => r.status === "earned").length,
    redeemedRewards: myRewards.filter(r => r.status === "redeemed").length,
    totalCashEarned: wallet.totalEarned,
    totalMonthsFree: myRewards.filter(r => r.type === "free_month" && r.status !== "expired").reduce((s, r) => s + r.value, 0),
  };

  return (
    <ReferralContext.Provider value={{
      referralCode, referralLink, referrals: myReferrals, rewards: myRewards,
      stats, wallet, addReferral, upgradeReferral, redeemReward, redeemWallet,
    }}>
      {children}
    </ReferralContext.Provider>
  );
}

export function useReferral() {
  const ctx = useContext(ReferralContext);
  if (!ctx) throw new Error("useReferral must be used within ReferralProvider");
  return ctx;
}
